/**
 * PixelBlock Python Runner.
 * Executes Python code in browser and desktop and streams output to console / terminal.
 */

function showInputPromptModal(promptText) {
    return new Promise(resolve => {
        if (typeof document === 'undefined') {
            resolve('');
            return;
        }

        const isFr = typeof window !== 'undefined' && (
            (window.pixelblockCurrentLocale && window.pixelblockCurrentLocale.startsWith('fr')) ||
            (navigator.language && navigator.language.startsWith('fr'))
        );

        // Backdrop overlay
        const overlay = document.createElement('div');
        overlay.style.position = 'fixed';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.width = '100vw';
        overlay.style.height = '100vh';
        overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.55)';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.zIndex = '9999999';
        overlay.style.fontFamily = '"Helvetica Neue", Helvetica, Arial, sans-serif';

        // Modal dialog container
        const modal = document.createElement('div');
        modal.style.background = '#ffffff';
        modal.style.borderRadius = '12px';
        modal.style.boxShadow = '0 16px 40px rgba(0, 0, 0, 0.3)';
        modal.style.width = '440px';
        modal.style.maxWidth = '90vw';
        modal.style.padding = '22px 24px';
        modal.style.boxSizing = 'border-box';
        modal.style.display = 'flex';
        modal.style.flexDirection = 'column';
        modal.style.gap = '14px';

        // Header
        const header = document.createElement('div');
        header.style.display = 'flex';
        header.style.alignItems = 'center';
        header.style.gap = '10px';
        header.style.fontSize = '16px';
        header.style.fontWeight = 'bold';
        header.style.color = '#1e293b';

        const iconSpan = document.createElement('span');
        iconSpan.textContent = '⌨️';
        iconSpan.style.fontSize = '18px';

        const titleSpan = document.createElement('span');
        titleSpan.textContent = isFr ? 'Entrée PixelBlocks' : 'PixelBlocks Input';

        header.appendChild(iconSpan);
        header.appendChild(titleSpan);

        // Prompt message
        const messageDiv = document.createElement('div');
        messageDiv.textContent = promptText || (isFr ? 'Veuillez saisir une valeur :' : 'Please enter a value:');
        messageDiv.style.fontSize = '14px';
        messageDiv.style.color = '#334155';
        messageDiv.style.lineHeight = '1.45';
        messageDiv.style.whiteSpace = 'pre-wrap';
        messageDiv.style.wordBreak = 'break-word';
        messageDiv.style.maxHeight = '120px';
        messageDiv.style.overflowY = 'auto';

        // Text input field
        const inputField = document.createElement('input');
        inputField.type = 'text';
        inputField.style.width = '100%';
        inputField.style.padding = '10px 14px';
        inputField.style.fontSize = '15px';
        inputField.style.border = '2px solid #4C97FF';
        inputField.style.borderRadius = '8px';
        inputField.style.outline = 'none';
        inputField.style.boxSizing = 'border-box';
        inputField.style.backgroundColor = '#f8fafc';
        inputField.style.color = '#1e293b';

        // Button row
        const btnRow = document.createElement('div');
        btnRow.style.display = 'flex';
        btnRow.style.justifyContent = 'flex-end';
        btnRow.style.gap = '10px';
        btnRow.style.marginTop = '6px';

        const cancelBtn = document.createElement('button');
        cancelBtn.type = 'button';
        cancelBtn.textContent = isFr ? 'Annuler' : 'Cancel';
        cancelBtn.style.padding = '8px 18px';
        cancelBtn.style.fontSize = '14px';
        cancelBtn.style.border = '1px solid #cbd5e1';
        cancelBtn.style.borderRadius = '8px';
        cancelBtn.style.background = '#f1f5f9';
        cancelBtn.style.color = '#475569';
        cancelBtn.style.cursor = 'pointer';
        cancelBtn.style.fontWeight = '500';

        const okBtn = document.createElement('button');
        okBtn.type = 'button';
        okBtn.textContent = isFr ? 'Valider' : 'OK';
        okBtn.style.padding = '8px 22px';
        okBtn.style.fontSize = '14px';
        okBtn.style.border = 'none';
        okBtn.style.borderRadius = '8px';
        okBtn.style.background = '#4C97FF';
        okBtn.style.color = '#ffffff';
        okBtn.style.cursor = 'pointer';
        okBtn.style.fontWeight = '600';

        btnRow.appendChild(cancelBtn);
        btnRow.appendChild(okBtn);

        modal.appendChild(header);
        modal.appendChild(messageDiv);
        modal.appendChild(inputField);
        modal.appendChild(btnRow);
        overlay.appendChild(modal);

        let isDone = false;
        const cleanup = (value) => {
            if (isDone) return;
            isDone = true;
            document.removeEventListener('keydown', onKeyDown);
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            resolve(value);
        };

        const onKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                cleanup(inputField.value);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cleanup('');
            }
        };

        okBtn.addEventListener('click', () => cleanup(inputField.value));
        cancelBtn.addEventListener('click', () => cleanup(''));
        document.addEventListener('keydown', onKeyDown);

        document.body.appendChild(overlay);
        setTimeout(() => {
            inputField.focus();
            inputField.select();
        }, 50);
    });
}

export function runPython(code, options = {}) {
    const {
        onOutput = () => {},
        onError = () => {},
        onFinish = () => {},
        turtleTarget = 'turtle-canvas'
    } = options;

    if (typeof window !== 'undefined' && window.Sk) {
        const Sk = window.Sk;

        if (!Sk._pixelblockInputPatched && Sk.builtin && Sk.builtin.raw_input) {
            const origRawInput = Sk.builtin.raw_input;
            Sk.builtin.raw_input = function (...args) {
                let prompt = "";
                if (args.length === 1) {
                    try {
                        prompt = args[0] ? Sk.ffi.remapToJs(args[0]) : "";
                    } catch (e) {
                        prompt = args[0] ? String(args[0].v !== undefined ? args[0].v : args[0]) : "";
                    }
                } else if (args.length > 1) {
                    prompt = args.map(a => {
                        try {
                            return String(Sk.ffi.remapToJs(a));
                        } catch (e) {
                            return a && a.v !== undefined ? String(a.v) : String(a);
                        }
                    }).join(" ");
                }
                return origRawInput.call(this, new Sk.builtin.str(prompt));
            };
            Sk.builtin.input = Sk.builtin.raw_input;
            Sk._pixelblockInputPatched = true;
        }

        Sk.configure({
            output: function (text) {
                onOutput(text);
            },
            read: function (file) {
                if (Sk.builtinFiles === undefined || Sk.builtinFiles.files[file] === undefined) {
                    throw new Error(`File not found: '${file}'`);
                }
                return Sk.builtinFiles.files[file];
            },
            inputfun: function (promptText) {
                if (promptText && typeof onOutput === "function") {
                    onOutput(promptText);
                }
                return showInputPromptModal(promptText).then(val => {
                    const result = val === null || val === undefined ? "" : String(val);
                    if (typeof onOutput === "function") {
                        onOutput(result + "\n");
                    }
                    return result;
                });
            },
            inputfunTakesPrompt: true
        });

        if (Sk.TurtleGraphics) {
            Sk.TurtleGraphics.target = turtleTarget;
        }

        const promise = Sk.misceval.asyncToPromise(() =>
            Sk.importMainWithBody('<stdin>', false, code, true)
        );

        return promise
            .then(() => {
                onFinish(null);
            })
            .catch(err => {
                const errMsg = err && err.toString ? err.toString() : String(err);
                onError(errMsg);
                onFinish(err);
            });
    }

    // Fallback simple evaluator for print, loops, arithmetic if Sk is loading
    try {
        onOutput('Executing Python code...\n');
        // Simple interpreter for basic prints
        const lines = code.split('\n');
        for (const line of lines) {
            const trimmed = line.trim();
            const pMatch = trimmed.match(/^print\((.*)\)$/);
            if (pMatch) {
                let content = pMatch[1].trim();
                if ((content.startsWith('"') && content.endsWith('"')) || (content.startsWith("'") && content.endsWith("'"))) {
                    content = content.slice(1, -1);
                }
                onOutput(content + '\n');
            }
        }
        onFinish(null);
        return Promise.resolve();
    } catch (e) {
        onError(e.message);
        onFinish(e);
        return Promise.reject(e);
    }
}
